import { useState } from 'react'
import { AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import { friendlyErrorMessage } from '@/lib/queries/_errors'

/**
 * Shared inline error block — replaces the `try/catch + toast.error('Failed
 * to load')` anti-pattern with a friendly message + retry button + optional
 * "show details" disclosure for admins / debugging.
 *
 * Use this for **content-loading failures** (Bible Insight panel, Prayer
 * generation, chapter load) where the user is staring at a blank space
 * waiting for content. For **operation failures** (chat send, save note,
 * save highlight) prefer `useToast().error(msg, { actionLabel: 'Retry',
 * onAction })` — toast surfaces the failure without hijacking the
 * surrounding UI.
 */
export default function ErrorState({
  error,
  onRetry,
  busy = false,
  compact = false,
  className = '',
}) {
  const [showDetails, setShowDetails] = useState(false)

  const friendly = friendlyErrorMessage(error)
  const detailText = error?.message && error.message !== friendly ? error.message : null
  const padding = compact ? 'p-3' : 'p-4'

  return (
    <div
      role="alert"
      className={`rounded-xl border border-red-700/30 bg-red-900/10 ${padding} ${className}`}
    >
      <div className="flex items-start gap-2.5">
        <AlertCircle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-100">{friendly}</p>
          {detailText && (
            <button
              type="button"
              onClick={() => setShowDetails(s => !s)}
              className="text-xs text-red-300/80 hover:text-red-200 mt-1 inline-flex items-center gap-1"
            >
              {showDetails ? (
                <ChevronUp className="w-3 h-3" aria-hidden="true" />
              ) : (
                <ChevronDown className="w-3 h-3" aria-hidden="true" />
              )}
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          )}
          {showDetails && detailText && (
            <pre className="text-xs mt-1.5 text-red-200/70 whitespace-pre-wrap break-words font-mono">
              {detailText}
            </pre>
          )}
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-700/30 hover:bg-red-700/40 text-red-100 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
            {busy ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  )
}
